import { useEffect, useState } from "react";
import { Plus, Trash2, Link as LinkIcon, StickyNote, Newspaper } from "lucide-react";
import { api } from "@/lib/apiClient";
import PageHeader from "@/components/PageHeader";

export default function News() {
    const [items, setItems] = useState([]);
    const [open, setOpen] = useState(false);
    const [form, setForm] = useState({ title: "", source_link: "", note: "", is_personal_note: true });

    const load = async () => {
        const { data } = await api.get("/news-items");
        setItems(data.sort((a, b) => (b.created_at || "").localeCompare(a.created_at || "")));
    };
    useEffect(() => { load(); }, []);

    const save = async (e) => {
        e.preventDefault();
        if (!form.title) return;
        await api.post("/news-items", form);
        setForm({ title: "", source_link: "", note: "", is_personal_note: true });
        setOpen(false);
        load();
    };
    const del = async (id) => { await api.delete(`/news-items/${id}`); load(); };

    return (
        <div className="space-y-8" data-testid="news-page">
            <PageHeader
                eyebrow="Clear-eyed context"
                title="News & Daily Brief"
                subtitle="Save sourced links and your own notes. Personal notes are clearly marked — this app never fabricates real-time news."
                action={
                    <button onClick={() => setOpen(!open)} className="bg-[#D4AF37] text-[#0E0E10] px-3 py-2 rounded-md hover:bg-[#B3922E] text-sm inline-flex items-center gap-2" data-testid="news-new-btn">
                        <Plus className="w-4 h-4" /> New entry
                    </button>
                }
            />

            {open && (
                <form onSubmit={save} className="twp-card p-6 space-y-3" data-testid="news-form">
                    <input placeholder="Title / headline / bias" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} required className="w-full bg-[#0e0e10] border border-white/10 rounded-md px-3 py-2 text-sm" data-testid="news-title" />
                    <input placeholder="Source link (optional)" value={form.source_link} onChange={(e) => setForm({ ...form, source_link: e.target.value })} className="w-full bg-[#0e0e10] border border-white/10 rounded-md px-3 py-2 text-sm" />
                    <textarea placeholder="Note or scenario (your words)" value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} rows={4} className="w-full bg-[#0e0e10] border border-white/10 rounded-md px-3 py-2 text-sm" data-testid="news-note" />
                    <label className="flex items-center gap-2 text-sm text-zinc-400">
                        <input type="checkbox" checked={form.is_personal_note} onChange={(e) => setForm({ ...form, is_personal_note: e.target.checked })} className="accent-[#D4AF37]" data-testid="news-is-note" />
                        This is a personal note (not a verified source)
                    </label>
                    <button type="submit" className="bg-[#D4AF37] text-[#0E0E10] px-4 py-2 rounded-md hover:bg-[#B3922E] text-sm" data-testid="news-save">Save</button>
                </form>
            )}

            <section className="grid md:grid-cols-2 gap-4" data-testid="news-list">
                {items.map((n) => (
                    <div key={n.id} className="twp-card p-5" data-testid={`news-${n.id}`}>
                        <div className="flex items-start justify-between gap-3">
                            <div className="flex items-center gap-2">
                                {n.is_personal_note ? <StickyNote className="w-4 h-4 gold" strokeWidth={1.5} /> : <Newspaper className="w-4 h-4 gold" strokeWidth={1.5} />}
                                <span className="caption">{n.is_personal_note ? "Personal note" : "Sourced"}</span>
                            </div>
                            <button onClick={() => del(n.id)} className="text-zinc-500 hover:text-[#B95050]"><Trash2 className="w-4 h-4" /></button>
                        </div>
                        <h3 className="font-serif text-lg mt-2">{n.title}</h3>
                        {n.note && <p className="text-sm text-zinc-400 mt-2 leading-relaxed">{n.note}</p>}
                        {n.source_link && (
                            <a href={n.source_link} target="_blank" rel="noopener noreferrer" className="mt-3 inline-flex items-center gap-1.5 text-xs text-[#D4AF37] hover:underline">
                                <LinkIcon className="w-3 h-3" /> Open source
                            </a>
                        )}
                    </div>
                ))}
                {items.length === 0 && <div className="md:col-span-2 text-sm text-zinc-500">No entries yet.</div>}
            </section>
        </div>
    );
}
